import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetCloseX,
} from "../../components/ui/sheet";
import {
  Check, ChevronRight, Plug, Package, Inbox, Activity,
  CircleDot, AlertTriangle, ArrowRight, FileSignature, Store,
  Sparkles, Lock, BadgeCheck, LayoutDashboard, TrendingUp,
  Wallet, Shield, Settings2, ShoppingBag, MessageCircle,
  MessageSquare, BarChart3, FileText, CreditCard, Heart, Warehouse,
  Truck, Search, Bell, ChevronDown, Banknote,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────
// Dropship redesign preview — IN-CONTEXT mockup at /preview/dropship-v2
//   Renders the new 4-tab Dropship inside a faithful portal-shell frame
//   (sidebar + topbar) for both Seller and Buyer roles. This is the exact
//   visual the user will see in the real app once we ship Move A + Move B.
//   Live /seller/dropship and /buyer/dropship are unchanged.
// ─────────────────────────────────────────────────────────────────────────

// ─── mock data ───────────────────────────────────────────────────────────
const MOCK_PARTNERS_SELLER = [
  { id: "p1", name: "Daisy & Co Boutique", channel: "Shopify",        orders30d: 142, gmv30d: 18450, health: "ok",    sync: "2m ago",  mapOk: true,  agreement: "Active" },
  { id: "p2", name: "Northside Pharmacy",  channel: "WooCommerce",    orders30d:  88, gmv30d:  9210, health: "ok",    sync: "12m ago", mapOk: true,  agreement: "Active" },
  { id: "p3", name: "Urban Tots",          channel: "Custom Webhook", orders30d:  41, gmv30d:  4780, health: "warn",  sync: "3h ago",  mapOk: false, agreement: "Active" },
  { id: "p4", name: "Coastal Mercantile",  channel: "Shopify",        orders30d:  12, gmv30d:  1340, health: "error", sync: "Failed",  mapOk: true,  agreement: "Active" },
];

const MOCK_PARTNERS_BUYER = [
  { id: "s1", name: "Verdant Naturals",      channel: "—", orders30d: 84, gmv30d: 12300, health: "ok",   sync: "Real-time", mapOk: true,  agreement: "Active" },
  { id: "s2", name: "Ridgeline Apparel Co.", channel: "—", orders30d: 56, gmv30d:  7820, health: "ok",   sync: "Real-time", mapOk: true,  agreement: "Active" },
  { id: "s3", name: "Sunbelt Snack Group",   channel: "—", orders30d: 22, gmv30d:  2110, health: "warn", sync: "Real-time", mapOk: false, agreement: "Pending amendment" },
];

const MOCK_ORDERS = [
  { id: "DS-21041", partner: "Daisy & Co Boutique", customer: "Anna L.", items: 3, total: 142.00, status: "Action needed", note: "Add tracking by 5pm",            sla: "2h left" },
  { id: "DS-21040", partner: "Daisy & Co Boutique", customer: "Marc T.", items: 1, total:  38.00, status: "In transit",    note: "UPS · 1Z9X4… · 2 days out",       sla: null },
  { id: "DS-21039", partner: "Northside Pharmacy",  customer: "Lila R.", items: 5, total: 211.00, status: "Delivered",     note: "Auto-invoice $211 · debited",     sla: null },
  { id: "DS-21038", partner: "Urban Tots",          customer: "Owen S.", items: 2, total:  76.00, status: "Stalled",       note: "No carrier movement 48h",         sla: "Open claim?" },
  { id: "DS-21037", partner: "Coastal Mercantile",  customer: "Jia W.",  items: 4, total: 189.00, status: "Claim",         note: "Damaged · refund pending",        sla: null },
];

const MOCK_CATALOG_SELLER = [
  { sku: "VRD-501", name: "Organic Bamboo Tee · M", msrp: 38, map: 30, wholesale: 14.50, handling: 1.20, mirrored: 3 },
  { sku: "VRD-502", name: "Organic Bamboo Tee · L", msrp: 38, map: 30, wholesale: 14.50, handling: 1.20, mirrored: 3 },
  { sku: "RDG-114", name: "Cabin Crew Socks · 3pk", msrp: 24, map: 19, wholesale:  8.00, handling: 0.90, mirrored: 2 },
  { sku: "SBT-301", name: "Maple Crunch Trail Mix", msrp: 12, map: 10, wholesale:  4.20, handling: 0.50, mirrored: 4 },
];

const MOCK_CATALOG_BUYER = [
  { sku: "VRD-501", name: "Organic Bamboo Tee · M", supplier: "Verdant Naturals",  wholesale: 14.50, msrp: 38, margin: 0.58, inStore: true  },
  { sku: "RDG-114", name: "Cabin Crew Socks · 3pk", supplier: "Ridgeline Apparel", wholesale:  8.00, msrp: 24, margin: 0.62, inStore: true  },
  { sku: "SBT-301", name: "Maple Crunch Trail Mix", supplier: "Sunbelt Snack",     wholesale:  4.20, msrp: 12, margin: 0.61, inStore: false },
];

// ─── sidebar shape (matches PortalShell visual conventions) ─────────────
const SIDEBAR_SELLER = [
  { label: "Dashboard", icon: LayoutDashboard },
  { label: "Buyers",    icon: TrendingUp },
  { label: "Orders",    icon: Package },
  { label: "Payouts",   icon: Wallet },
  { label: "Insurance", icon: Shield },
  { label: "Settings",  icon: Settings2 },
];
const SIDEBAR_SELLER_APPS = [
  { label: "Catalog",  icon: Package },
  { label: "Messages", icon: MessageCircle },
  { label: "Dropship", icon: ShoppingBag, active: true },
  { label: "RFQ",      icon: MessageSquare },
  { label: "Reports",  icon: BarChart3 },
];
const SIDEBAR_BUYER = [
  { label: "Dashboard",         icon: LayoutDashboard },
  { label: "Orders",            icon: FileText },
  { label: "Payments",          icon: CreditCard },
  { label: "Shipping",          icon: Truck },
  { label: "Score",             icon: TrendingUp },
  { label: "PO Inbox",          icon: Inbox },
  { label: "Invoice financing", icon: Banknote },
];
const SIDEBAR_BUYER_APPS = [
  { label: "Catalog",   icon: ShoppingBag },
  { label: "Messages",  icon: MessageCircle },
  { label: "Suppliers", icon: Heart },
  { label: "Dropship",  icon: Package, active: true },
  { label: "RFQ",       icon: MessageSquare },
  { label: "Inventory", icon: Warehouse },
  { label: "Reports",   icon: BarChart3 },
];

// ─── helpers ─────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500 mb-3">{children}</div>
);

const StatTile = ({ label, value, hint, accent = "default" }) => (
  <Card className="p-4 bg-white border border-zinc-200/80">
    <div className="text-xs text-zinc-500 font-medium">{label}</div>
    <div className={`mt-1 text-2xl font-semibold tracking-tight ${accent === "warn" ? "text-amber-600" : accent === "error" ? "text-red-600" : "text-zinc-900"}`}>
      {value}
    </div>
    {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
  </Card>
);

const HealthPill = ({ kind, children }) => {
  const map = {
    ok:    "bg-emerald-50 text-emerald-700 ring-emerald-200",
    warn:  "bg-amber-50 text-amber-700 ring-amber-200",
    error: "bg-red-50 text-red-700 ring-red-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ring-1 ${map[kind] || map.ok}`}>
      <CircleDot className="w-3 h-3" /> {children}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    "Action needed": "bg-amber-50 text-amber-700 ring-amber-200",
    "In transit":    "bg-blue-50 text-blue-700 ring-blue-200",
    "Delivered":     "bg-emerald-50 text-emerald-700 ring-emerald-200",
    "Stalled":       "bg-orange-50 text-orange-700 ring-orange-200",
    "Claim":         "bg-red-50 text-red-700 ring-red-200",
  };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ring-1 ${map[status] || "bg-zinc-50 text-zinc-700 ring-zinc-200"}`}>{status}</span>;
};

// ─── Tabs ────────────────────────────────────────────────────────────────
function ConnectTab({ role }) {
  const steps = role === "seller"
    ? [
        { n: 1, title: "Invite a buyer", desc: "Send an invite to a retailer or import from your buyer list.", icon: Store, done: true },
        { n: 2, title: "Sign dropship agreement", desc: "MAP, returns, handling fee, payment terms. Both signatures required.", icon: FileSignature, done: true },
        { n: 3, title: "Mirror catalog", desc: "Pick products to expose. Set MSRP, MAP, wholesale and per-order handling.", icon: Package, done: false, current: true },
      ]
    : [
        { n: 1, title: "Find your supplier", desc: "Search Verqo's directory or accept an incoming invite.", icon: Store, done: true },
        { n: 2, title: "Sign dropship agreement", desc: "MAP, returns, handling fee, payment terms. Both signatures required.", icon: FileSignature, done: true },
        { n: 3, title: "Mirror catalog to your storefront", desc: "Add supplier's products to your store with one click — margins shown live.", icon: Package, done: true },
        { n: 4, title: "Connect your sales channel", desc: "Shopify · WooCommerce · Amazon · Walmart · TikTok · Custom webhook · CSV", icon: Plug, done: false, current: true },
      ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold tracking-tight">Set up dropship with a new partner</h3>
          <p className="text-sm text-zinc-500 mt-0.5">
            {role === "seller" ? "3 steps · ~5 min." : "4 steps · ~7 min."} This tab auto-hides once you have at least one active relationship.
          </p>
        </div>
        <Button variant="outline" size="sm">Skip for now</Button>
      </div>

      <div className="space-y-3">
        {steps.map(s => {
          const Icon = s.icon;
          return (
            <Card key={s.n} className={`p-4 flex items-center gap-4 ${s.current ? "ring-2 ring-zinc-900/10 bg-zinc-50/40" : "bg-white"}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${s.done ? "bg-emerald-100 text-emerald-700" : s.current ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500"}`}>
                {s.done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{s.n}. {s.title}</span>
                  {s.done && <Badge variant="secondary" className="text-[10px]">Done</Badge>}
                  {s.current && <Badge className="bg-zinc-900 text-white text-[10px]">Next</Badge>}
                </div>
                <div className="text-sm text-zinc-500 mt-0.5">{s.desc}</div>
              </div>
              {s.current && <Button size="sm">Open <ArrowRight className="w-4 h-4 ml-1" /></Button>}
            </Card>
          );
        })}
      </div>

      <Card className="p-4 bg-zinc-900 text-white border-0">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-amber-300 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold">Agreement-first by design</div>
            <p className="text-zinc-300 mt-1">
              The agreement locks MAP, handling fee, returns and payment terms before any product or order can flow.
              {role === "seller" ? " Verqo is your source of truth — no channel needed on your side." : " You bring the storefront; we keep prices and inventory in sync."}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function OrdersTab({ role, onOpenPartner }) {
  const [chip, setChip] = useState("all");
  const chips = [
    { id: "all",     label: "All" },
    { id: "action",  label: "Action needed" },
    { id: "transit", label: "In transit" },
    { id: "done",    label: "Delivered" },
    { id: "stall",   label: "Stalled" },
    { id: "claim",   label: "Claims" },
  ];

  const filtered = chip === "all" ? MOCK_ORDERS :
    chip === "action"  ? MOCK_ORDERS.filter(o => o.status === "Action needed") :
    chip === "transit" ? MOCK_ORDERS.filter(o => o.status === "In transit") :
    chip === "done"    ? MOCK_ORDERS.filter(o => o.status === "Delivered") :
    chip === "stall"   ? MOCK_ORDERS.filter(o => o.status === "Stalled") :
    MOCK_ORDERS.filter(o => o.status === "Claim");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold tracking-tight">Order pipeline</h3>
        <div className="text-xs text-zinc-500">{MOCK_ORDERS.length} orders · last 7 days</div>
      </div>

      <div className="flex flex-wrap gap-2">
        {chips.map(c => (
          <button
            key={c.id}
            onClick={() => setChip(c.id)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full ring-1 transition ${
              chip === c.id
                ? "bg-zinc-900 text-white ring-zinc-900"
                : "bg-white text-zinc-700 ring-zinc-200 hover:ring-zinc-300"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <Card className="bg-white border border-zinc-200/80 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/80 text-zinc-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">Order</th>
              <th className="text-left font-medium px-4 py-2.5">{role === "seller" ? "Buyer" : "Supplier"}</th>
              <th className="text-left font-medium px-4 py-2.5">Customer</th>
              <th className="text-right font-medium px-4 py-2.5">Total</th>
              <th className="text-left font-medium px-4 py-2.5">Status</th>
              <th className="text-left font-medium px-4 py-2.5">Activity</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id} className="border-t border-zinc-100 hover:bg-zinc-50/50">
                <td className="px-4 py-3 font-mono text-xs text-zinc-700">{o.id}</td>
                <td className="px-4 py-3">
                  <button onClick={() => onOpenPartner(o.partner)} className="text-zinc-900 hover:underline">
                    {o.partner}
                  </button>
                </td>
                <td className="px-4 py-3 text-zinc-700">{o.customer}</td>
                <td className="px-4 py-3 text-right tabular-nums">${o.total.toFixed(2)}</td>
                <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{o.note}</td>
                <td className="px-4 py-3 text-right">
                  {o.sla
                    ? <Button size="sm" variant={o.status === "Action needed" ? "default" : "outline"}>{o.sla}</Button>
                    : <ChevronRight className="w-4 h-4 text-zinc-300 inline" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-3 bg-zinc-50 border-dashed border-zinc-300">
        <div className="flex items-center gap-2 text-xs text-zinc-600">
          <BadgeCheck className="w-4 h-4 text-emerald-600" />
          Invoice events appear inline on each order row — the separate &quot;Auto-Invoices&quot; tab is gone.
        </div>
      </Card>
    </div>
  );
}

function CatalogTab({ role }) {
  const isSeller = role === "seller";
  const rows = isSeller ? MOCK_CATALOG_SELLER : MOCK_CATALOG_BUYER;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold tracking-tight">{isSeller ? "My dropship catalog" : "Available from my suppliers"}</h3>
          <p className="text-sm text-zinc-500 mt-0.5">
            {isSeller
              ? "Source of truth for MSRP · MAP · Wholesale · Handling. Mirrored to buyer channels."
              : "One-click add to your storefront. Margins shown live; MAP automatically enforced."}
          </p>
        </div>
        <Button variant="outline" size="sm">{isSeller ? "Import from Shopify" : "Sync catalog"}</Button>
      </div>

      <Card className="bg-white border border-zinc-200/80 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/80 text-zinc-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">SKU</th>
              <th className="text-left font-medium px-4 py-2.5">Product</th>
              {isSeller ? (
                <>
                  <th className="text-right font-medium px-4 py-2.5">MSRP</th>
                  <th className="text-right font-medium px-4 py-2.5">MAP</th>
                  <th className="text-right font-medium px-4 py-2.5">Wholesale</th>
                  <th className="text-right font-medium px-4 py-2.5">Handling</th>
                  <th className="text-right font-medium px-4 py-2.5">Mirrored</th>
                </>
              ) : (
                <>
                  <th className="text-left  font-medium px-4 py-2.5">Supplier</th>
                  <th className="text-right font-medium px-4 py-2.5">Wholesale</th>
                  <th className="text-right font-medium px-4 py-2.5">MSRP</th>
                  <th className="text-right font-medium px-4 py-2.5">Margin</th>
                  <th className="text-right font-medium px-4 py-2.5">In store</th>
                </>
              )}
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.sku} className="border-t border-zinc-100 hover:bg-zinc-50/50">
                <td className="px-4 py-3 font-mono text-xs">{r.sku}</td>
                <td className="px-4 py-3">{r.name}</td>
                {isSeller ? (
                  <>
                    <td className="px-4 py-3 text-right tabular-nums">${r.msrp.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">${r.map.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">${r.wholesale.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">${r.handling.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right"><Badge variant="secondary" className="text-[10px]">{r.mirrored} buyers</Badge></td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 text-zinc-700">{r.supplier}</td>
                    <td className="px-4 py-3 text-right tabular-nums">${r.wholesale.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">${r.msrp.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">{Math.round(r.margin * 100)}%</td>
                    <td className="px-4 py-3 text-right">
                      {r.inStore ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Live</Badge> : <Button size="sm" variant="outline" className="h-6 text-[11px]">Add</Button>}
                    </td>
                  </>
                )}
                <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-zinc-300 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function HealthTab({ role, onOpenPartner }) {
  const partners = role === "seller" ? MOCK_PARTNERS_SELLER : MOCK_PARTNERS_BUYER;
  const claims = [
    { id: "CL-902", partner: "Coastal Mercantile", reason: "Damaged on arrival",  status: "Refund pending", age: "1d" },
    { id: "CL-901", partner: "Urban Tots",         reason: "No carrier movement", status: "Investigating",  age: "2d" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <SectionLabel>Today at a glance</SectionLabel>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile label="Active relationships" value={partners.length} hint={`${partners.filter(p => p.health === "ok").length} healthy`} />
          <StatTile label="Orders · last 30d"     value={partners.reduce((s, p) => s + p.orders30d, 0)} hint="+18% vs prev 30d" />
          <StatTile label="GMV · last 30d"        value={`$${partners.reduce((s, p) => s + p.gmv30d, 0).toLocaleString()}`} />
          <StatTile label="Sync errors"           value={partners.filter(p => p.health === "error").length} accent={partners.filter(p => p.health === "error").length > 0 ? "error" : "default"} hint="Last 24h" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>Relationships</SectionLabel>
          <span className="text-xs text-zinc-500">Click a row → relationship detail</span>
        </div>
        <Card className="bg-white border border-zinc-200/80 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/80 text-zinc-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium px-4 py-2.5">{role === "seller" ? "Buyer" : "Supplier"}</th>
                {role === "seller" && <th className="text-left font-medium px-4 py-2.5">Channel</th>}
                <th className="text-right font-medium px-4 py-2.5">Orders 30d</th>
                <th className="text-right font-medium px-4 py-2.5">GMV 30d</th>
                <th className="text-left font-medium px-4 py-2.5">Last sync</th>
                <th className="text-left font-medium px-4 py-2.5">MAP</th>
                <th className="text-left font-medium px-4 py-2.5">Agreement</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {partners.map(p => (
                <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50/60 cursor-pointer" onClick={() => onOpenPartner(p.name)}>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  {role === "seller" && <td className="px-4 py-3 text-zinc-700">{p.channel}</td>}
                  <td className="px-4 py-3 text-right tabular-nums">{p.orders30d}</td>
                  <td className="px-4 py-3 text-right tabular-nums">${p.gmv30d.toLocaleString()}</td>
                  <td className="px-4 py-3"><HealthPill kind={p.health}>{p.sync}</HealthPill></td>
                  <td className="px-4 py-3">
                    {p.mapOk
                      ? <Badge variant="secondary" className="text-[10px]"><Check className="w-3 h-3 mr-1 inline" /> Clean</Badge>
                      : <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]"><AlertTriangle className="w-3 h-3 mr-1 inline" /> Violation</Badge>}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-700">{p.agreement}</td>
                  <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-zinc-400 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <div>
        <SectionLabel>Open claims · {claims.length}</SectionLabel>
        <div className="space-y-2">
          {claims.map(c => (
            <Card key={c.id} className="p-4 flex items-center gap-4 bg-white">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{c.id} · {c.reason}</div>
                <div className="text-xs text-zinc-500">{c.partner} · {c.age} open</div>
              </div>
              <Badge variant="secondary" className="text-[11px]">{c.status}</Badge>
              <Button variant="outline" size="sm">Open</Button>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Move B: Relationship detail drawer ─────────────────────────────────
function RelationshipDrawer({ partnerName, role, open, onClose }) {
  const [innerTab, setInnerTab] = useState("agreement");
  if (!partnerName) return null;

  const tabs = [
    { id: "agreement", label: "Agreement", icon: FileSignature },
    { id: "catalog",   label: "Catalog",   icon: Package },
    { id: "orders",    label: "Orders",    icon: Inbox },
    { id: "messages",  label: "Messages",  icon: Activity },
  ];

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 overflow-y-auto bg-zinc-50">
        <SheetHeader className="p-6 pb-4 bg-white border-b border-zinc-200">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs text-zinc-500 mb-1">{role === "seller" ? "Buyer relationship" : "Supplier relationship"}</div>
              <SheetTitle className="text-2xl tracking-tight">{partnerName}</SheetTitle>
              <div className="flex items-center gap-2 mt-2">
                <HealthPill kind="ok">Synced · 2m ago</HealthPill>
                <Badge variant="secondary" className="text-[10px]"><Lock className="w-3 h-3 mr-1 inline" /> Agreement locked</Badge>
              </div>
            </div>
            <SheetCloseX />
          </div>
        </SheetHeader>

        <div className="px-6 py-4 bg-white border-b border-zinc-200 grid grid-cols-3 gap-3">
          <div>
            <div className="text-[11px] text-zinc-500 uppercase tracking-wide">Orders 30d</div>
            <div className="text-lg font-semibold tabular-nums">142</div>
          </div>
          <div>
            <div className="text-[11px] text-zinc-500 uppercase tracking-wide">GMV 30d</div>
            <div className="text-lg font-semibold tabular-nums">$18,450</div>
          </div>
          <div>
            <div className="text-[11px] text-zinc-500 uppercase tracking-wide">Credit used</div>
            <div className="text-lg font-semibold tabular-nums">$4,212 <span className="text-xs text-zinc-500 font-normal">/ $15K</span></div>
          </div>
        </div>

        <div className="px-6 py-3 bg-white border-b border-zinc-200 flex gap-1.5 overflow-x-auto">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setInnerTab(t.id)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ring-1 transition whitespace-nowrap ${
                  innerTab === t.id ? "bg-zinc-900 text-white ring-zinc-900" : "bg-white text-zinc-700 ring-zinc-200 hover:ring-zinc-300"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-6 space-y-4">
          {innerTab === "agreement" && (
            <Card className="p-4 bg-white">
              <div className="text-xs text-zinc-500 uppercase tracking-wide mb-3">Terms</div>
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                <div className="text-zinc-500">Payment terms</div>     <div className="font-medium">Net 30</div>
                <div className="text-zinc-500">Handling fee</div>      <div className="font-medium">$1.20 / order</div>
                <div className="text-zinc-500">MAP enforcement</div>   <div className="font-medium">Strict · violations auto-flagged</div>
                <div className="text-zinc-500">Returns window</div>    <div className="font-medium">14 days</div>
                <div className="text-zinc-500">Damaged shipments</div> <div className="font-medium">100% credit + reship if photo &lt; 7d</div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Badge variant="secondary" className="text-[10px]"><Lock className="w-3 h-3 mr-1 inline" /> Locked since Jan 12, 2026</Badge>
                <Button size="sm" variant="outline">Propose amendment</Button>
              </div>
            </Card>
          )}
          {innerTab === "catalog" && (
            <Card className="p-4 bg-white">
              <div className="text-xs text-zinc-500 uppercase tracking-wide mb-3">Mirrored to this partner</div>
              <div className="space-y-2">
                {MOCK_CATALOG_SELLER.slice(0, 3).map(p => (
                  <div key={p.sku} className="flex items-center justify-between text-sm border border-zinc-100 rounded-lg px-3 py-2">
                    <div>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{p.sku}</div>
                    </div>
                    <div className="text-right text-xs text-zinc-700">
                      MSRP ${p.msrp} · MAP ${p.map} · WS ${p.wholesale}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {innerTab === "orders" && (
            <Card className="bg-white overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {MOCK_ORDERS.slice(0, 4).map(o => (
                    <tr key={o.id} className="border-b border-zinc-100 last:border-0">
                      <td className="px-4 py-3 font-mono text-xs">{o.id}</td>
                      <td className="px-4 py-3">{o.customer}</td>
                      <td className="px-4 py-3 tabular-nums">${o.total.toFixed(2)}</td>
                      <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
          {innerTab === "messages" && (
            <Card className="p-4 bg-white space-y-3">
              <div className="text-xs text-zinc-500 uppercase tracking-wide">Recent thread</div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-medium">DC</div>
                <div className="flex-1 bg-zinc-50 rounded-lg px-3 py-2 text-sm">
                  <div className="font-medium text-xs text-zinc-700 mb-0.5">{partnerName}</div>
                  Any ETA on DS-21041? Customer is asking.
                  <div className="text-[10px] text-zinc-400 mt-1">2h ago</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-medium">YOU</div>
                <div className="flex-1 bg-zinc-900 text-white rounded-lg px-3 py-2 text-sm">
                  Pulled tracking just now — UPS scan delayed. I&apos;ll add it manually by 5pm.
                  <div className="text-[10px] text-zinc-400 mt-1">1h ago</div>
                </div>
              </div>
              <Button size="sm" variant="outline" className="w-full">Open full conversation</Button>
            </Card>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Portal-shell mockup (sidebar + topbar) ─────────────────────────────
function PortalChrome({ role, setRole, children }) {
  const coreNav = role === "seller" ? SIDEBAR_SELLER : SIDEBAR_BUYER;
  const appsNav = role === "seller" ? SIDEBAR_SELLER_APPS : SIDEBAR_BUYER_APPS;
  const portalName = role === "seller" ? "Seller portal · verqo" : "Buyer portal · verqo";
  const userInitials = role === "seller" ? "VN" : "DC";
  const userName = role === "seller" ? "Verdant Naturals" : "Daisy & Co Boutique";

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      {/* Preview banner */}
      <div className="bg-zinc-900 text-white text-[11px] px-6 py-1.5 flex items-center gap-2 flex-shrink-0">
        <Sparkles className="w-3 h-3 text-amber-300" />
        Non-functional preview · live <span className="font-mono">/seller/dropship</span> & <span className="font-mono">/buyer/dropship</span> unchanged
        <span className="ml-auto inline-flex rounded-full bg-white/10 p-0.5">
          <button onClick={() => setRole("seller")} className={`text-[11px] px-3 py-0.5 rounded-full ${role === "seller" ? "bg-white text-zinc-900 font-semibold" : "text-zinc-300"}`} data-testid="role-seller">Seller</button>
          <button onClick={() => setRole("buyer")}  className={`text-[11px] px-3 py-0.5 rounded-full ${role === "buyer"  ? "bg-white text-zinc-900 font-semibold" : "text-zinc-300"}`} data-testid="role-buyer">Buyer</button>
        </span>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="w-60 bg-white border-r border-zinc-200 flex-shrink-0 flex flex-col">
          <div className="px-4 h-14 flex items-center gap-2 border-b border-zinc-100">
            <div className="w-7 h-7 rounded-md bg-zinc-900 text-white flex items-center justify-center text-xs font-bold">v</div>
            <div>
              <div className="text-[11px] font-semibold leading-tight">verqo</div>
              <div className="text-[10px] text-zinc-500 leading-tight">{role === "seller" ? "Seller" : "Buyer"}</div>
            </div>
            <ChevronDown className="w-3.5 h-3.5 ml-auto text-zinc-400" />
          </div>

          <nav className="flex-1 p-3 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 px-3 mb-2">Core</div>
            <div className="space-y-0.5 mb-4">
              {coreNav.map(item => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] text-zinc-600 hover:bg-zinc-50">
                    <Icon className="w-4 h-4" /> {item.label}
                  </div>
                );
              })}
            </div>

            <div className="text-[10px] uppercase tracking-widest text-zinc-400 px-3 mb-2">Apps</div>
            <div className="space-y-0.5">
              {appsNav.map(item => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className={`flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] ${item.active ? "bg-zinc-900 text-white font-medium" : "text-zinc-600 hover:bg-zinc-50"}`}>
                    <Icon className="w-4 h-4" /> {item.label}
                  </div>
                );
              })}
            </div>
          </nav>

          <div className="border-t border-zinc-100 p-3 flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center text-[11px] font-semibold">{userInitials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-medium truncate">{userName}</div>
              <div className="text-[10px] text-zinc-500 truncate">{role === "seller" ? "demo.seller@verqo.com" : "demo.buyer@verqo.com"}</div>
            </div>
          </div>
        </aside>

        {/* Main column */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Topbar */}
          <header className="h-14 bg-white border-b border-zinc-200 px-6 flex items-center gap-4 flex-shrink-0">
            <div className="text-xs text-zinc-500">{portalName}</div>
            <div className="text-zinc-300">·</div>
            <div className="text-xs">
              <span className="text-zinc-500">Apps</span>
              <span className="text-zinc-400 mx-1">/</span>
              <span className="text-zinc-900 font-medium">Dropship</span>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 bg-zinc-50 ring-1 ring-zinc-200 rounded-md px-2.5 py-1.5">
                <Search className="w-3.5 h-3.5 text-zinc-400" />
                <span className="text-xs text-zinc-400">Search…</span>
                <kbd className="text-[10px] bg-white px-1 rounded text-zinc-400 ring-1 ring-zinc-200">⌘K</kbd>
              </div>
              <Bell className="w-4 h-4 text-zinc-500" />
              <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center text-[11px] font-semibold">{userInitials}</div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 overflow-y-auto p-6 lg:p-8">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────
export default function DropshipV2Preview() {
  const [role, setRole] = useState("seller");
  const [tab, setTab] = useState("orders");
  const [partner, setPartner] = useState(null);

  const tabs = [
    { id: "connect", label: "Connect", icon: Plug,     desc: "Onboarding · 4 chapters of the dropship story start here" },
    { id: "orders",  label: "Orders",  icon: Inbox,    desc: "Daily pipeline — orders flow, you ship, invoice posts inline" },
    { id: "catalog", label: "Catalog", icon: Package,  desc: "Source-of-truth products · pricing · MAP · handling fees" },
    { id: "health",  label: "Health",  icon: Activity, desc: "Relationships · KPIs · claims — the GROW chapter" },
  ];

  return (
    <PortalChrome role={role} setRole={setRole}>
      {/* Dropship page header */}
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">App · Dropship</div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {role === "seller" ? "Sell to retailers without holding their inventory" : "Add supplier products to your storefront in minutes"}
            </h1>
            <p className="text-sm text-zinc-600 mt-1.5 max-w-2xl">
              {role === "seller"
                ? "Mirror your catalog to as many buyers as you want. Verqo handles agreements, MAP enforcement, order routing, invoices and claims."
                : "Mirror supplier catalogs to your Shopify, Woo, Amazon or custom channel. Verqo handles agreements, pricing, sync, invoices and claims."}
            </p>
          </div>
          <div className="text-xs text-zinc-500 flex items-center gap-4">
            <span>4 active relationships</span>
            <span className="text-zinc-300">·</span>
            <HealthPill kind="ok">All synced</HealthPill>
          </div>
        </div>
      </div>

      {/* The 4-chapter story strip */}
      <Card className="p-4 bg-white border border-zinc-200/80 mb-5">
        <div className="grid grid-cols-4 gap-2 text-[11px]">
          {[
            { k: "CONNECT once",     v: role === "seller" ? "Invite buyer · Sign · Mirror catalog" : "Find supplier · Sign · Mirror catalog · Add channel" },
            { k: "OPERATE every day", v: "Order flows in · You ship · Tracking auto-pushes" },
            { k: "RECONCILE",         v: "Invoice posts · Wallet debits · Claims if broken" },
            { k: "GROW",              v: "See what sells, what stalls, scale to more partners" },
          ].map((c, i) => (
            <div key={i} className={`px-3 py-2 rounded-lg ${tabs[i].id === tab ? "bg-zinc-900 text-white" : "bg-zinc-50 text-zinc-700"}`}>
              <div className={`font-semibold ${tabs[i].id === tab ? "text-amber-300" : "text-zinc-500"} mb-0.5`}>{c.k}</div>
              <div className="leading-snug">{c.v}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-zinc-200/80 overflow-hidden">
        <div className="flex border-b border-zinc-100">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex-1 px-5 py-3.5 text-left border-b-2 transition ${
                  active ? "border-zinc-900 bg-zinc-50/50" : "border-transparent hover:bg-zinc-50/40"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${active ? "text-zinc-900" : "text-zinc-500"}`} />
                  <span className={`text-sm font-semibold ${active ? "text-zinc-900" : "text-zinc-700"}`}>{t.label}</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{t.desc}</div>
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {tab === "connect" && <ConnectTab role={role} />}
          {tab === "orders"  && <OrdersTab  role={role} onOpenPartner={setPartner} />}
          {tab === "catalog" && <CatalogTab role={role} />}
          {tab === "health"  && <HealthTab  role={role} onOpenPartner={setPartner} />}
        </div>
      </div>

      <RelationshipDrawer
        partnerName={partner}
        role={role}
        open={!!partner}
        onClose={() => setPartner(null)}
      />
    </PortalChrome>
  );
}
